import { useEffect, useState } from "react";

import { Plus, Search, UserCheck, UserMinus, UserX } from "lucide-react";

import type {
  ApprovalEntryResponseSchema,
  VinciCandidateSchema,
} from "@/api/generated/types.gen";
import { SpotlightSearch } from "@/components/CasesList/components/SpotlightSearch";
import { PageHeader } from "@/components/PageHeader";
import { UserIdentity } from "@/components/UserIdentity";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useCreateAccessEntry,
  useDeleteAccessEntry,
  useUpdateAccessEntry,
} from "@/hooks/mutations/use-access-list";
import { useAccessList, useVinciSearch } from "@/hooks/queries/use-access-list";
import { useMe } from "@/hooks/queries/use-me";
import { toTitleCase } from "@/utils/string";

/** Sentinel for the Select's "no filter" option — Radix rejects an empty-string value. */
const ALL_STATUSES = "all";

/** Debounce a changing value by `delay` ms. */
const useDebounced = <T,>(value: T, delay = 300): T => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const statusVariant = (status: string) =>
  status === "active" ? "success" : "warning";

const AddUserModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<VinciCandidateSchema | null>(null);
  const [error, setError] = useState<string | null>(null);

  const debouncedQuery = useDebounced(query);
  const { data: candidates = [], isFetching } = useVinciSearch({
    q: debouncedQuery,
    enabled: !selected,
  });
  const createEntry = useCreateAccessEntry();

  // Reset the form whenever the modal opens (or closes) — matches
  // CreateTemplateModal's isOpen-keyed reset (design: Report Studio UI Update).
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setSelected(null);
      setError(null);
    }
  }, [isOpen]);

  const handleAdd = async () => {
    if (!selected) return;
    setError(null);
    try {
      await createEntry.mutateAsync({
        email: selected.email,
        user_type: selected.user_type,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add user.");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription className="sr-only">
            Search Vinci for a user and add them to the approval list.
          </DialogDescription>
        </DialogHeader>

        {selected ? (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-background-muted/50 p-3">
            <div className="min-w-0 flex-1">
              <UserIdentity
                name={selected.name}
                email={selected.email}
                meta={toTitleCase(selected.user_type)}
                showAvatar={false}
              />
            </div>
            {selected.is_shared_inbox && (
              <Badge variant="warning">Shared inbox — grants everyone on it</Badge>
            )}
            <button
              type="button"
              className="font-head text-xs font-semibold text-foreground-muted underline-offset-2 hover:text-foreground hover:underline"
              onClick={() => setSelected(null)}
            >
              Change
            </button>
          </div>
        ) : (
          <div className="relative flex flex-col gap-1">
            <Input
              placeholder="Search Vinci by name or email (min 2 chars)…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              leftIcon={<Search className="size-4 text-foreground-muted" />}
              autoFocus
            />
            {debouncedQuery.trim().length >= 2 && (
              <div className="max-h-64 overflow-auto rounded-lg border border-border">
                {isFetching && (
                  <div className="p-3 text-sm text-foreground-muted">Searching…</div>
                )}
                {!isFetching && candidates.length === 0 && (
                  <div className="p-3 text-sm text-foreground-muted">
                    No matching Vinci users.
                  </div>
                )}
                {candidates.map((c) => (
                  <button
                    type="button"
                    key={`${c.user_type}-${c.vinci_id}`}
                    className="flex w-full items-center justify-between gap-2 p-3 text-left hover:bg-background-muted/50"
                    onClick={() => setSelected(c)}
                  >
                    <UserIdentity
                      name={c.name}
                      email={c.email}
                      meta={toTitleCase(c.user_type)}
                      showAvatar={false}
                    />
                    {c.is_shared_inbox && <Badge variant="warning">shared</Badge>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="simple-outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            disabled={!selected || createEntry.isPending}
            isLoading={createEntry.isPending}
          >
            Add to approval list
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EntryRow = ({ entry }: { entry: ApprovalEntryResponseSchema }) => {
  const update = useUpdateAccessEntry();
  const remove = useDeleteAccessEntry();

  const isSuspended = entry.status === "suspended";

  return (
    <TableRow className="hover:bg-[#E8FDFF]/50">
      <TableCell>
        <div className="flex items-center gap-2">
          <UserIdentity name={entry.name} email={entry.email} />
          {entry.is_shared_inbox && <Badge variant="warning">shared</Badge>}
        </div>
      </TableCell>
      <TableCell className="text-foreground-muted">
        {toTitleCase(entry.user_type)}
      </TableCell>
      <TableCell>
        <Badge variant={statusVariant(entry.status)}>
          {toTitleCase(entry.status)}
        </Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button
            variant={isSuspended ? "success-outline" : "simple-outline"}
            size="sm"
            onClick={() =>
              update.mutate({
                entryId: entry.id,
                body: { status: isSuspended ? "active" : "suspended" },
              })
            }
            disabled={update.isPending}
          >
            {isSuspended ? (
              <UserCheck className="size-4" />
            ) : (
              <UserX className="size-4" />
            )}
            {isSuspended ? "Activate" : "Suspend"}
          </Button>
          <Button
            variant="destructive-ghost"
            size="sm"
            onClick={() => {
              if (window.confirm(`Remove ${entry.email} from the approval list?`)) {
                remove.mutate(entry.id);
              }
            }}
            disabled={remove.isPending}
          >
            <UserMinus className="size-4" />
            Remove
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const AccessControlPage = () => {
  const { data: me, isLoading: meLoading } = useMe();
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const debouncedSearch = useDebounced(search);

  const isAdmin = Boolean(me?.user?.is_admin);
  const { data: entries = [], isLoading } = useAccessList({
    status: statusFilter || undefined,
    q: debouncedSearch || undefined,
    enabled: isAdmin,
  });

  const hasActiveFilters = Boolean(search || statusFilter);
  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
  };

  if (meLoading) {
    return <div className="px-4 py-4 text-foreground-muted">Loading…</div>;
  }
  if (!isAdmin) {
    return (
      <div className="px-4 py-4 text-foreground-muted">
        You don’t have access to this page.
      </div>
    );
  }

  return (
    <div className="space-y-2 p-2 pl-0">
      <div className="rounded-2xl border border-foreground-muted/20 bg-background-white p-4">
        <PageHeader
          title="Access Control"
          description="Approved users can sign in to Report Builder with an email code. Being in Vinci is necessary but not sufficient — this list is the gate."
          rightContent={
            <Button
              size="sm"
              className="shadow-[0_6px_16px_rgba(26,25,57,0.22)]"
              onClick={() => setIsAddUserOpen(true)}
            >
              <Plus className="size-4" />
              Add user
            </Button>
          }
        />
      </div>

      <div className="rounded-2xl border border-foreground-muted/20 bg-background-white">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold tracking-[-0.01em]">
              Approved users
            </h2>
            <Badge variant="secondary">{entries.length}</Badge>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-[280px] shrink-0">
              <SpotlightSearch
                value={search}
                onChange={setSearch}
                placeholder="Search by name or email"
              />
            </div>
            <Select
              value={statusFilter || ALL_STATUSES}
              onValueChange={(value) =>
                setStatusFilter(value === ALL_STATUSES ? "" : value)
              }
            >
              <SelectTrigger size="sm" className="min-w-[160px] data-[size=sm]:h-10">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_STATUSES}>All statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <button
                type="button"
                className="font-head text-xs font-semibold text-foreground-muted underline-offset-2 hover:text-foreground hover:underline"
                onClick={clearFilters}
              >
                Clear all
              </button>
            )}
          </div>
        </div>

        <Table className="border-t border-foreground-muted/20">
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>User type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4}>Loading…</TableCell>
              </TableRow>
            ) : entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  {hasActiveFilters ? "No matching users." : "No entries yet."}
                </TableCell>
              </TableRow>
            ) : (
              entries.map((entry) => <EntryRow key={entry.id} entry={entry} />)
            )}
          </TableBody>
        </Table>
      </div>

      <AddUserModal isOpen={isAddUserOpen} onClose={() => setIsAddUserOpen(false)} />
    </div>
  );
};

export default AccessControlPage;
